import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export type AppRole = "customer" | "solo" | "center_admin" | "provider";

interface UserRoleInfo {
  role: AppRole | null;
  isLoading: boolean;
  isCustomer: boolean;
  isSolo: boolean;
  isCenterAdmin: boolean;
  isProvider: boolean;
  isBusinessOwner: boolean;
  canManagePractitioners: boolean;
  canManageInventory: boolean;
  canViewReports: boolean;
  refetch: () => Promise<void>;
}

export function useUserRole(): UserRoleInfo {
  const { user, profile, loading: authLoading } = useAuth();
  const [role, setRole] = useState<AppRole | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchRole = async () => {
    if (!user) {
      setRole(null);
      setIsLoading(false);
      return;
    }

    // Use profile role directly - it's synced with user_roles via trigger
    // This avoids type issues with the new user_roles table not being in types yet
    if (profile?.role) {
      setRole(profile.role as AppRole);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (!authLoading) {
      fetchRole();
    }
  }, [user, profile, authLoading]);

  // Computed role checks
  const isCustomer = role === "customer";
  const isSolo = role === "solo";
  const isCenterAdmin = role === "center_admin";
  const isProvider = role === "provider";
  
  // Business owner = solo practitioner or center admin
  const isBusinessOwner = isSolo || isCenterAdmin;
  
  // Only center admins can manage multiple practitioners
  const canManagePractitioners = isCenterAdmin;
  
  // Business owners can manage inventory
  const canManageInventory = isBusinessOwner;
  
  // Business owners can view reports
  const canViewReports = isBusinessOwner;

  return {
    role,
    isLoading: isLoading || authLoading,
    isCustomer,
    isSolo,
    isCenterAdmin,
    isProvider,
    isBusinessOwner,
    canManagePractitioners,
    canManageInventory,
    canViewReports,
    refetch: fetchRole,
  };
}
